import { html, nothing, PropertyValueMap } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import environment from '../../common/environment';
import { connect } from 'pwa-helpers';
import store from '../../store/store';
import { PassValidationCompStatus, spinner } from '../../common/constants/api-constants';
import { initUpdateProfileWithAffiliation, updatePassValidationCompStatus } from '../user-profile';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { FormBaseComponent } from '../base-component/forms/form-base-component';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { StoreState } from '../../store/store.interface';
import {
  getAnalyticsSafeData,
  selectPasswordValidationStatus,
  selectUser,
} from '../user-profile/user-profile.selectors';
import { validateUserPassword } from '../user-profile/thunks/validate-user-password.thunk';
import emit from '../../common/events';
import { getAppFlow, getUserActiveTab } from '../user-profile/utils/user-profile.util';
import { UserProfile } from '../user-profile/user-profile.interface';
import { AccountPageAppFlow } from '../my-account/app-flow.enum';

@customElement('mhecomm-password-validation')
export class PasswordValidation extends connect(store)(FormBaseComponent) {
  @state()
  private compState: PassValidationCompStatus = PassValidationCompStatus.default;

  private resetPasswordLinkId = 'my-account-reset-password-link';
  private resetPasswordLinkText = "Don't have a password yet?";

  private validatePasswordButtonId = 'my-account-validate-password-button';
  private validatePasswordButtonText = 'Enter';

  private cancelButtonId = 'my-account-cancel-password-validation-button';
  private cancelButtonText = 'Cancel';

  private user: UserProfile | undefined;

  get passwordEl() {
    return this.shadowRoot?.querySelector('#passwordValidation') as HTMLInputElement;
  }

  stateChanged(state: StoreState) {
    this.user = selectUser(state);
    this.compState = selectPasswordValidationStatus(state);
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(_changedProperties);
    if (![PassValidationCompStatus.succeeded].includes(this.compState)) {
      this.passwordEl?.focus();
    }
  }

  renderComp() {
    if (this.compState === PassValidationCompStatus.default) {
      return html`<slot></slot>`;
    } else if (![PassValidationCompStatus.succeeded].includes(this.compState)) {
      return html`
        <!-- 
          please note to accomplish the required design we have used grid styling inside
          ux-css-thememing repo (filepath: src/themes/mhcorp/forms.scss).
          in case you are modifing any elements position or adding or removing any element
          we need to updated its style there as well.
          Note: why: i had to impl the style in uxt becasue media query were not working.
        -->
        <form class="password-validation-form needs-validation align-items-start" @submit=${this.submit} novalidate>
          <div class="label-container">
            <label class="form-label fw-normal mb-0 p-2 bg-light border rounded" for="passwordValidation"
              >Enter your current password to make changes
            </label>
            <br />
            ${this.isFa4Flow()
              ? html` <a
                  id="${this.resetPasswordLinkId}"
                  href="${this.resetPasswordLink()}"
                  target="_blank"
                  class="btn btn-link text-decoration-none"
                  aria-label="${ariaLabelWithNewTab(this.resetPasswordLinkText)}"
                  disabled=${ifDefined(this.isSubmitted || undefined)}
                  @click=${this.onClickResetPasswordLink}
                >
                  ${this.resetPasswordLinkText}
                </a>`
              : nothing}
          </div>
          <div class="w-100">
            <input class="form-control" id="passwordValidation" name="password" type="password" required />
            ${this.compState === PassValidationCompStatus.failed
              ? html`<p class="order-sm-5 m-0 order-sm-5" id="passwordValidation-field-error" role="alert">
                  <span class="text-danger">
                    ${this.uxtIconReady ? html`<uxt-icon class="me-2" icon="exclamation-circle"></uxt-icon>` : nothing}
                    Incorrect password</span
                  >
                </p>`
              : nothing}
          </div>
          <button
            id="${this.validatePasswordButtonId}"
            class="btn btn-primary w-100 w-sm-auto"
            data-automation-id="submit-validate-password"
            disabled=${ifDefined(this.isSubmitted || undefined)}
          >
            ${this.isSubmitted ? spinner({ size: 'sm' }) : this.validatePasswordButtonText}
          </button>
          <button
            type="button"
            @click=${() => this.onCancelClick()}
            disabled=${ifDefined(this.isSubmitted || undefined)}
            class="btn btn-outline-primary w-100 w-sm-auto"
          >
            ${this.cancelButtonText}
          </button>
        </form>
      `;
    } else {
      return nothing;
    }
  }

  onClickResetPasswordLink(event: Event) {
    event.preventDefault();
    const link = event.currentTarget as HTMLAnchorElement;
    emit({
      element: this,
      name: 'click',
      detail: {
        id: this.resetPasswordLinkId,
        label: this.resetPasswordLinkText,
        comp: 'link',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        href: link.href,
        ...(getUserActiveTab() !== '' && { myAccountSection: getUserActiveTab() })
      },
      dataLayer: true,
    });
    window.location.href = link.href;
  }

  onCancelClick() {
    emit({
      element: this,
      name: 'click',
      detail: {
        id: this.cancelButtonId,
        label: this.cancelButtonText,
        comp: 'button',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        myAccountSection: getUserActiveTab(),
      },
      dataLayer: true,
    });
    this.updateCompStatus(PassValidationCompStatus.default);
  }

  updateCompStatus(status: PassValidationCompStatus) {
    store.dispatch(updatePassValidationCompStatus(status));
  }

  private onCancelClick() {
    this.updateCompStatus(PassValidationCompStatus.default);
    const tabShadowRoot = this.getRootNode() as ShadowRoot;
    requestAnimationFrame(() => {
      tabShadowRoot
        .querySelector('mhecomm-my-information-form')
        ?.shadowRoot
        ?.querySelector<HTMLElement>('#my-account-edit-information-button')
        ?.focus({ focusVisible: true });
    });
  }

  showError() {
    const element = this.passwordEl;
    if (element) {
      element.setAttribute('aria-describedby', `${element.id}-field-error`);
      element.setAttribute('aria-invalid', 'true');
    }
  }

  protected validationMessage() {
    return {
      passwordValidation: 'Please enter the password',
    };
  }

  resetPasswordLink() {
    if (this.isFa4Flow()) {
      return `${environment().firstAidForwadAppUrl}/mg/password-assistance?loginUrl=https%253A%252F%252Ffirstaid.mheducation.com%252Faccount%252Fedit&app=firstaid.mheducation.com`;
    } else {
      return `${environment().shopUrl}/password-assistance`;
    }
  }

  isFa4Flow() {
    return getAppFlow() === AccountPageAppFlow.Dfa;
  }

  async submit(e) {
    super.submit(e);
    if (this.isSubmitted) {
      return;
    }

    if (await this.isFormValid()) {
      this.isSubmitted = true;
      try {
        const userData = this.getRawValue();
        await store.dispatch(validateUserPassword(userData)).unwrap();
        store.dispatch(initUpdateProfileWithAffiliation())
        emit({
          element: this,
          name: 'submit',
          detail: {
            id: this.validatePasswordButtonId,
            label: this.validatePasswordButtonText,
            comp: 'button',
            user: getAnalyticsSafeData(this.user!),
            appFlow: getAppFlow(),
            myAccountSection: getUserActiveTab(),
          },
          dataLayer: true,
        });
      } catch (e) {
        console.error(e);
        this.showError();
      } finally {
        this.isSubmitted = false;
      }
    }
  }

  protected createErrorElement(element: HTMLInputElement) {
    return `
      <div class="alert alert-danger py-2 fs-6 invalid-feedback d-block order-sm-5" role="alert" aria-live="assertive" id="${element.id}-field-error">
        <uxt-icon icon="exclamation-triangle"></uxt-icon><span class="visually-hidden">Error&nbsp;</span>${element.validationMessage}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-password-validation': PasswordValidation;
  }
}
